import React from 'react';
import { motion } from 'framer-motion';
import { Category, ErrorOutline } from '@mui/icons-material';
import { ProductGroup } from '../../models/ProductGroup';
import ProductGroupCard from './ProductGroupCard';

interface ProductGroupListProps {
  groups: ProductGroup[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalGroups: number;
  onPageChange: (page: number) => void;
  onEditGroup: (group: ProductGroup) => void;
  onRefresh: () => void;
  adminMode?: boolean;
}

const ProductGroupList: React.FC<ProductGroupListProps> = ({
  groups,
  isLoading,
  error,
  currentPage,
  totalPages,
  totalGroups,
  onPageChange,
  onEditGroup,
  onRefresh,
  adminMode = true
}) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg p-5 animate-pulse border border-gray-100"
          >
            <div className="flex justify-between">
              <div className="w-1/3 h-4 bg-gray-200 rounded"></div>
              <div className="w-1/4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="mt-3 w-1/5 h-3 bg-gray-200 rounded"></div>
            <div className="mt-4 space-y-2">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-red-50 border border-red-200 rounded-lg p-6 text-center"
      >
        <div className="flex justify-center items-center mb-4">
          <ErrorOutline className="text-red-500 text-4xl" />
        </div>
        <h3 className="text-lg font-semibold text-red-700 mb-2">
          Wystąpił błąd
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <motion.button
          onClick={onRefresh}
          className="px-4 py-2 bg-white text-red-600 border border-red-300 rounded-md hover:bg-red-50"
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          Spróbuj ponownie
        </motion.button>
      </motion.div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center"
      >
        <div className="flex justify-center items-center mb-4">
          <Category className="text-gray-400 text-5xl" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          Brak grup produktowych
        </h3>
        <p className="text-gray-500">
          {adminMode 
            ? "Nie znaleziono żadnych grup produktowych. Kliknij przycisk 'Dodaj nową grupę', aby utworzyć pierwszą grupę."
            : "Nie znaleziono żadnych grup produktowych."}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {groups.map((group) => (
        <motion.div key={group.id} variants={itemVariants}>
          <ProductGroupCard 
            group={group} 
            onEdit={() => onEditGroup(group)}
            adminMode={adminMode} 
          />
        </motion.div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <div className="flex items-center gap-2">
            <motion.button
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              className={`px-5 py-3 rounded-full flex items-center justify-center ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              variants={buttonVariants}
              whileHover={currentPage === 1 ? {} : "hover"}
              whileTap={currentPage === 1 ? {} : "tap"}
            >
              &laquo; Poprzednia
            </motion.button>

            <div className="px-4 py-2 bg-white border border-gray-200 rounded-md text-gray-700">
              Strona {currentPage} z {totalPages}
            </div>

            <motion.button
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
              className={`px-5 py-3 rounded-full flex items-center justify-center ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              variants={buttonVariants}
              whileHover={currentPage === totalPages ? {} : "hover"}
              whileTap={currentPage === totalPages ? {} : "tap"}
            >
              Następna &raquo;
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ProductGroupList;
